<template>
  <layout inverse>
    <div class="OtherPage">
      <ai-hero
        class="OtherPage-hero OtherPage-hero--desktop"
        :hero-title="otherPage?.title as string"
        :hero-description="otherPage?.subtitle as string"
        :hero-image="otherPage?.heroImage"
        :breadcrumb-items="breadcrumbs"
        :partial-height="isSmallDevice ? '100vh' : '85vh'"
        variant="inverse"
        home-page>
        <template #header>
          <div class="OtherPage-hero-header">
            <ai-separator :margin="2" />
          </div>
        </template>
      </ai-hero>

      <ai-hero
        class="OtherPage-hero OtherPage-hero--mobile"
        :hero-title="otherPage?.title as string"
        :hero-description="otherPage?.subtitle as string"
        :hero-image="otherPage?.heroImage"
        :breadcrumb-items="breadcrumbs"
        variant="inverse"
        home-page>
        <template #header>
          <div class="OtherPage-hero-header">
            <ai-separator :margin="2" />
          </div>
        </template>
      </ai-hero>

      <div class="Layout OtherPage-content">
        <div class="Layout-section OtherPage-content-introduction">
          <ai-separator class="OtherPage-separator" inverse />

          <ai-typo as="h1" variant="heading-03">
            {{ otherPage?.introduction }}
          </ai-typo>

          <ai-separator
            class="OtherPage-separator OtherPage-separator"
            inverse />
        </div>

        <section class="Layout-section">
          <div class="OtherPage-body">
            <ai-rich-text
              :document="otherPage?.content"
              class="OtherPage-richText" />

            <ai-button
              v-if="otherPage?.cta"
              :label="otherPage?.cta?.label as string"
              :url="otherPage?.cta?.url as string"
              target="_blank"
              icon-right="button-arrow-right"
              variant="secondary"
              as-link />
          </div>
        </section>
      </div>
    </div>
  </layout>
</template>

<script setup lang="ts">
import axios from 'axios';
import type { RouteLocationRaw } from 'vue-router';

import { useOtherPageAsyncData } from '~~/domains/graphql';
import { useHeaderConfiguration } from '~~/domains/ux';
import { getRawBackendUrl } from '~~/helpers/backend-url.helper';

const route = useRoute();

const { data, error } = await useOtherPageAsyncData({
  slug: route.params.slug as string,
});
useAsyncDataError(error, 'other-page-slug');

const { t } = useI18n();

useSeoMetadata({
  metadata: data.value?.otherPage?.seo,
  fallback: {
    title: t('seo.otherPage.title', { name: data.value?.otherPage?.title }),
    description: data.value?.otherPage?.subtitle,
  },
});

definePageMeta({
  async validate(routeToValidate) {
    const slug = routeToValidate.params.slug;
    const isPreview = routeToValidate.query.draft === 'true';
    const language = routeToValidate.fullPath.slice(1, 3);
    const serverUri = getRawBackendUrl();

    try {
      const response = await axios.get(
        `${serverUri}/content/other-page/by-slug`,
        {
          headers: {
            'x-preview': isPreview,
          },
          params: { slug, language },
        },
      );

      return response.data;
    } catch (err) {
      return false;
    }
  },
});

const otherPage = computed(() => data.value?.otherPage);

const localePath = useLocalePath();

const isSmallDevice = useMatchMediaQuery('tablet');

const breadcrumbs = computed(() => [
  { label: t('navigation.page.home'), route: localePath({ name: 'index' }) },
  {
    label: otherPage.value?.title as string,
    route: localePath(route),
    currentPage: true,
  },
]);

// Locale switcher override
const routeLocalization = computed(() => {
  const payload = {} as Record<string, RouteLocationRaw>;

  for (const [language, path] of Object.entries(
    otherPage?.value?.slugByLanguage || {},
  )) {
    payload[language] = {
      name: 'other-page-slug',
      params: { slug: path as string },
    };
  }

  return payload;
});

useHeaderConfiguration({
  routeLocalization: routeLocalization.value,
});
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/functions' as func;
@use '@/assets/styles/utilities/mq';

/* .OtherPage-hero {
  height: func.calcRem(590);

  @media (mq.$from-medium) {
    height: func.calcRem(560);
  }
} */
.OtherPage-hero-header {
  margin-top: constants.$header-large;
}

.OtherPage-content {
  position: relative;

  @media (mq.$from-medium) {
    margin-top: calc(#{func.calcRem(120)} * -1);
  }
}

.OtherPage-content-introduction {
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: colors.$white;
  justify-content: center;
  box-sizing: border-box;
  text-align: center;
  padding: 0 constants.$padding-02;

  @media (mq.$from-medium) {
    padding: 0 constants.$padding-07;
  }
}

.OtherPage-body {
  box-sizing: border-box;
  padding-bottom: constants.$padding-06;
  padding-inline: constants.$padding-02;

  @media (mq.$from-medium) {
    padding-inline: constants.$padding-07;
  }
}

.OtherPage-separator {
  width: func.calcRem(32);

  display: inline-block;
  @media (mq.$from-medium) {
    width: func.calcRem(96);
  }
}

.OtherPage-hero--desktop {
  display: none;

  @media (mq.$from-medium) {
    display: inherit;
  }
}

.OtherPage-hero--mobile {
  @media (mq.$from-medium) {
    display: none;
  }
}
</style>

<style lang="scss">
@use '@/assets/styles/utilities/constants';

.OtherPage-body {
  h4 {
    margin-bottom: constants.$margin-00;
  }

  p {
    margin-bottom: constants.$margin-05;
  }
}
</style>
